import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { ConfirmModal } from "../components/shared/ConfirmModal";
import { TableSkeletonRows } from "../components/ui/Skeleton";
import {
  automationsApi,
  AutomationRuleDto,
  CreateAutomationRuleRequest,
} from "../api/automationsApi";

const TRIGGER_OPTIONS = [
  { value: "DisputeAge", label: "Dispute age threshold", desc: "When a dispute letter was sent and the item is still open past N days." },
  { value: "NewClient", label: "New client added", desc: "Clients created within the last N days." },
  { value: "ScoreChange", label: "Credit score change", desc: "When a bureau score improves vs the prior period." },
  { value: "StatusChange", label: "Dispute status updated", desc: "Disputes updated in the last 24 hours." },
  { value: "Inactivity", label: "Client inactivity", desc: "Client record not updated for N+ days." },
  { value: "DisputeSent", label: "Dispute letter sent (legacy)", desc: "Letter sent in the last 24 hours." },
  { value: "ResponseReceived", label: "Bureau response (legacy)", desc: "Response logged in the last 24 hours." },
  { value: "ClientAdded", label: "New client window (legacy)", desc: "Same as new client; uses N-day window." },
  { value: "DaysWithoutUpdate", label: "Stale dispute (legacy)", desc: "Open dispute not updated for N+ days." },
  { value: "ScoreImproved", label: "Score improved (legacy)", desc: "Same as score change detection." },
] as const;

const ACTION_OPTIONS = [
  { value: "CreateTask", label: "Create a task", desc: "Creates an in-app task notification for the client." },
  { value: "SendEmail", label: "Send email", desc: "Sends email when the client has an address on file." },
  { value: "SendNotification", label: "In-app notification", desc: "Creates a notification entry for staff workflow." },
  { value: "FlagForReview", label: "Flag for review", desc: "Flags the client for manual review." },
  { value: "SendSms", label: "Send SMS", desc: "Texts the client when a phone number exists." },
  { value: "GenerateLetter", label: "Generate letter prompt", desc: "Creates a letter-generation reminder notification." },
] as const;

type TriggerValue = (typeof TRIGGER_OPTIONS)[number]["value"];
type ActionValue = (typeof ACTION_OPTIONS)[number]["value"];

function needsDaysThreshold(triggerType: string): boolean {
  return (
    triggerType === "DisputeAge" ||
    triggerType === "NewClient" ||
    triggerType === "ClientAdded" ||
    triggerType === "DaysWithoutUpdate" ||
    triggerType === "Inactivity"
  );
}

function triggerDescription(triggerType: string): string {
  return TRIGGER_OPTIONS.find((t) => t.value === triggerType)?.desc ?? triggerType;
}

function actionDescription(actionType: string): string {
  return ACTION_OPTIONS.find((a) => a.value === actionType)?.desc ?? actionType;
}

function daysFromRule(r: AutomationRuleDto): string {
  const cfg = r.triggerConfig;
  if (cfg && typeof cfg === "object" && "daysThreshold" in cfg) {
    const v = (cfg as { daysThreshold?: number }).daysThreshold;
    if (typeof v === "number" && v > 0) return String(v);
  }
  return r.triggerValue || "";
}

type FormState = {
  name: string;
  triggerType: string;
  daysThreshold: string;
  actionType: string;
  actionTemplate: string;
  isActive: boolean;
};

const emptyForm: FormState = {
  name: "",
  triggerType: "DisputeAge",
  daysThreshold: "30",
  actionType: "CreateTask",
  actionTemplate:
    "Client {{ClientName}} — follow up on open dispute. Creditor: {{Creditor}}. Status: {{DisputeStatus}}.",
  isActive: true,
};

const QUICK_TEMPLATES: { title: string; blurb: string; form: FormState }[] = [
  {
    title: "30-day follow-up reminder",
    blurb: "Open disputes with a letter sent 30+ days ago → create a task.",
    form: {
      name: "30-day follow-up reminder",
      triggerType: "DisputeAge",
      daysThreshold: "30",
      actionType: "CreateTask",
      actionTemplate:
        "Follow up: {{ClientName}} — dispute pending 30+ days. Creditor: {{Creditor}}. Status: {{DisputeStatus}}.",
      isActive: true,
    },
  },
  {
    title: "Welcome new clients",
    blurb: "Clients added in the last 7 days → send a welcome email.",
    form: {
      name: "Welcome new clients",
      triggerType: "NewClient",
      daysThreshold: "7",
      actionType: "SendEmail",
      actionTemplate: "Welcome {{ClientName}}! We're glad you're here and will keep you updated on every dispute step.",
      isActive: true,
    },
  },
  {
    title: "Score change alert",
    blurb: "When a bureau score improves → in-app notification.",
    form: {
      name: "Score change alert",
      triggerType: "ScoreChange",
      daysThreshold: "",
      actionType: "SendNotification",
      actionTemplate: "Score improved for {{ClientName}}. Review credit analysis and notify the client if appropriate.",
      isActive: true,
    },
  },
];

function buildPayload(form: FormState): CreateAutomationRuleRequest {
  const days = form.daysThreshold.trim();
  const triggerValue = needsDaysThreshold(form.triggerType) ? days : "";
  const triggerConfig =
    needsDaysThreshold(form.triggerType) && days
      ? { daysThreshold: Math.max(1, parseInt(days, 10) || 1) }
      : undefined;

  return {
    name: form.name.trim(),
    triggerType: form.triggerType,
    triggerValue,
    actionType: form.actionType,
    actionTemplate: form.actionTemplate,
    isActive: form.isActive,
    ...(triggerConfig ? { triggerConfig } : {}),
  };
}

function parseApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error && typeof data.error === "string") {
      return data.error;
    }
  }
  return fallback;
}

const AutomationsPage: React.FC = () => {
  const [rules, setRules] = useState<AutomationRuleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AutomationRuleDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [processBusy, setProcessBusy] = useState(false);
  const [processMsg, setProcessMsg] = useState<string | null>(null);
  const [deleteRuleTarget, setDeleteRuleTarget] = useState<{ id: string; name: string } | null>(null);

  const selectedTrigger = useMemo(
    () => TRIGGER_OPTIONS.find((t) => t.value === form.triggerType),
    [form.triggerType],
  );
  const selectedAction = useMemo(
    () => ACTION_OPTIONS.find((a) => a.value === form.actionType),
    [form.actionType],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await automationsApi.list();
      setRules(data);
    } catch {
      setError("Could not load automation rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const applyTemplate = (t: (typeof QUICK_TEMPLATES)[number]) => {
    setEditing(null);
    setForm({ ...t.form });
    setModalOpen(true);
  };

  const openEdit = (r: AutomationRuleDto) => {
    setEditing(r);
    setForm({
      name: r.name,
      triggerType: r.triggerType,
      daysThreshold: needsDaysThreshold(r.triggerType) ? daysFromRule(r) : "",
      actionType: r.actionType,
      actionTemplate: r.actionTemplate,
      isActive: r.isActive,
    });
    setModalOpen(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload(form);
      if (editing) {
        await automationsApi.update(editing.id, {
          name: payload.name,
          triggerType: payload.triggerType,
          triggerValue: payload.triggerValue,
          actionType: payload.actionType,
          actionTemplate: payload.actionTemplate,
          isActive: payload.isActive,
          ...(payload.triggerConfig !== undefined ? { triggerConfig: payload.triggerConfig } : {}),
        });
      } else {
        await automationsApi.create(payload);
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(parseApiError(err, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemoveRule = async () => {
    const target = deleteRuleTarget;
    setDeleteRuleTarget(null);
    if (!target) {
      return;
    }
    setError(null);
    try {
      await automationsApi.delete(target.id);
      await load();
    } catch {
      setError("Delete failed.");
    }
  };

  const toggle = async (r: AutomationRuleDto) => {
    try {
      await automationsApi.toggle(r.id);
      await load();
    } catch {
      setError("Could not toggle rule.");
    }
  };

  const runProcess = async () => {
    setProcessBusy(true);
    setProcessMsg(null);
    setError(null);
    try {
      const r = await automationsApi.process();
      setProcessMsg(`Processed ${r.rulesEvaluated} rule(s), ${r.actionsTriggered} action(s).`);
    } catch {
      setError("Process run failed.");
    } finally {
      setProcessBusy(false);
    }
  };

  const formatWhen = (iso?: string | null) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return "—";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-white">Automations</h1>
            <p className="mt-1 text-sm text-slate-400">
              Configure tenant-scoped rules: triggers like dispute age or new clients, and actions such as tasks, email, or
              notifications.
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={() => openCreate()}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 sm:w-auto"
            >
              Create automation
            </button>
            <button
              type="button"
              disabled={processBusy}
              onClick={() => void runProcess()}
              className="w-full rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 disabled:opacity-50 sm:w-auto"
            >
              {processBusy ? "Running…" : "Run automations now"}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm text-red-200">{error}</div>
        )}
        {processMsg && (
          <div className="rounded-lg border border-emerald-900/40 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-100">
            {processMsg}
          </div>
        )}

        <section className="rounded-xl border border-slate-800 bg-slate-900/50">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="px-5 py-6">
                <TableSkeletonRows rows={6} cols={7} />
              </div>
            ) : rules.length === 0 ? (
              <div className="space-y-6 px-5 py-10">
                <div>
                  <h2 className="text-lg font-medium text-white">No automations configured yet</h2>
                  <p className="mt-2 max-w-2xl text-sm text-slate-400">
                    Automations run on your tenant&apos;s data only. Start from a template or create a custom rule. Execution can be
                    run on demand below; scheduled runners can be added later.
                  </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <button
                    type="button"
                    onClick={() => openCreate()}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 sm:w-auto"
                  >
                    Create your first automation
                  </button>
                </div>
                <div>
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500">Suggested templates</p>
                  <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {QUICK_TEMPLATES.map((t) => (
                      <li
                        key={t.title}
                        className="rounded-lg border border-slate-800 bg-slate-950/50 p-4 text-left shadow-sm"
                      >
                        <p className="font-medium text-white">{t.title}</p>
                        <p className="mt-1 text-xs text-slate-500">{t.blurb}</p>
                        <button
                          type="button"
                          onClick={() => applyTemplate(t)}
                          className="mt-3 text-xs font-semibold text-blue-400 hover:text-blue-300"
                        >
                          Use template
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <table className="w-full min-w-[1024px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="px-5 py-3 font-medium">Name</th>
                    <th className="px-5 py-3 font-medium">When</th>
                    <th className="px-5 py-3 font-medium">Then</th>
                    <th className="px-5 py-3 font-medium">Last run</th>
                    <th className="px-5 py-3 font-medium">Runs</th>
                    <th className="px-5 py-3 font-medium">Active</th>
                    <th className="px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80">
                  {rules.map((r) => (
                    <tr key={r.id} className="text-slate-300">
                      <td className="px-5 py-3 font-medium text-white">{r.name}</td>
                      <td className="max-w-xs px-5 py-3 text-slate-400">
                        <span className="text-xs text-slate-500">{r.triggerType}</span>
                        <p className="mt-0.5 text-xs leading-snug">{triggerDescription(r.triggerType)}</p>
                      </td>
                      <td className="max-w-xs px-5 py-3">
                        <span className="text-xs text-slate-500">{r.actionType}</span>
                        <p className="mt-0.5 text-xs leading-snug text-slate-400">{actionDescription(r.actionType)}</p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-xs text-slate-500">{formatWhen(r.lastTriggeredAt)}</td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-400">{r.triggerCount ?? 0}</td>
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={r.isActive}
                          onClick={() => void toggle(r)}
                          className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full border px-0.5 transition-colors ${
                            r.isActive ? "border-emerald-700 bg-emerald-900/60" : "border-slate-600 bg-slate-800"
                          }`}
                        >
                          <span
                            className={`pointer-events-none block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                              r.isActive ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="mr-3 text-xs font-medium text-blue-400 hover:text-blue-300"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteRuleTarget({ id: r.id, name: r.name })}
                          className="text-xs font-medium text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {modalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-white">{editing ? "Edit automation" : "New automation"}</h3>
              <form onSubmit={(e) => void save(e)} className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400">Name</label>
                  <input
                    required
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400">Trigger</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                    value={form.triggerType}
                    onChange={(e) => setForm((f) => ({ ...f, triggerType: e.target.value as TriggerValue }))}
                  >
                    {TRIGGER_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  {selectedTrigger && (
                    <p className="mt-1 text-xs text-slate-500">{selectedTrigger.desc}</p>
                  )}
                </div>
                {needsDaysThreshold(form.triggerType) && (
                  <div>
                    <label className="block text-xs font-medium text-slate-400">Days threshold</label>
                    <input
                      type="number"
                      min={1}
                      required={needsDaysThreshold(form.triggerType)}
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                      value={form.daysThreshold}
                      onChange={(e) => setForm((f) => ({ ...f, daysThreshold: e.target.value }))}
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-slate-400">Action</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-white"
                    value={form.actionType}
                    onChange={(e) => setForm((f) => ({ ...f, actionType: e.target.value as ActionValue }))}
                  >
                    {ACTION_OPTIONS.map((a) => (
                      <option key={a.value} value={a.value}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  {selectedAction && <p className="mt-1 text-xs text-slate-500">{selectedAction.desc}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400">
                    Message template — {"{{ClientName}}"}, {"{{Creditor}}"}, {"{{DisputeStatus}}"}, etc.
                  </label>
                  <textarea
                    required
                    rows={5}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 font-mono text-sm text-white"
                    value={form.actionTemplate}
                    onChange={(e) => setForm((f) => ({ ...f, actionTemplate: e.target.value }))}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  />
                  Active
                </label>
                <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="w-full rounded-lg px-4 py-2 text-sm text-slate-400 hover:bg-slate-800 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50 sm:w-auto"
                  >
                    {saving ? "Saving…" : editing ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmModal
          isOpen={deleteRuleTarget !== null}
          title="Delete Automation"
          message={
            deleteRuleTarget
              ? `Are you sure you want to delete "${deleteRuleTarget.name}"? This action cannot be undone.`
              : ""
          }
          confirmLabel="Delete"
          confirmVariant="danger"
          onConfirm={() => void confirmRemoveRule()}
          onCancel={() => setDeleteRuleTarget(null)}
        />
      </div>
    </DashboardLayout>
  );
};

export default AutomationsPage;
